import { useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { AuthContext } from "../../AuthContext";
import {
  configureUserAnalytics,
  flushUserAnalytics,
  getPageAnalyticsName,
  normalizeAnalyticsName,
  trackUserAction,
} from "../../utils/userAnalytics";

const INTERACTIVE_SELECTOR = "[data-analytics-name],button,a,[role='button']";
const MODAL_SELECTOR = "[role='dialog'],.MuiModal-root";

const cleanText = (value, maxLength = 160) => String(value || "").replace(/\s+/g, " ").trim().slice(0, maxLength);

const getElementLabel = (element) => {
  const iconName = element.querySelector("[data-testid$='Icon']")?.getAttribute("data-testid")?.replace(/Icon$/, "");
  const href = element instanceof HTMLAnchorElement ? element.getAttribute("href") : "";
  return cleanText(
    element.dataset.analyticsName ||
    element.getAttribute("aria-label") ||
    element.getAttribute("title") ||
    element.textContent ||
    element.getAttribute("name") ||
    element.id ||
    iconName ||
    href,
    120,
  ) || "unknown";
};

const isVisibleModal = (element) => {
  if (!(element instanceof Element) || !element.isConnected) return false;
  if (element.matches(".MuiModal-hidden,[aria-hidden='true']")) return false;
  if (element.closest(".MuiModal-hidden,[aria-hidden='true']")) return false;
  const style = window.getComputedStyle(element);
  return style.display !== "none" && style.visibility !== "hidden";
};

const getModalTitle = (element) => {
  const explicit = element.dataset.analyticsName || element.closest("[data-analytics-name]")?.dataset.analyticsName;
  if (explicit) return explicit;
  const labelledBy = element.getAttribute("aria-labelledby");
  if (labelledBy) {
    const label = document.getElementById(labelledBy)?.textContent?.trim();
    if (label) return label;
  }
  return (
    element.getAttribute("aria-label") ||
    element.querySelector("h1,h2,h3,h4,.MuiDialogTitle-root")?.textContent?.trim() ||
    "modal"
  );
};

const getInteractionContext = (element, pageName) => {
  const dialog = element.closest("[role='dialog'],.MuiDialog-root,.MuiModal-root");
  if (dialog) {
    const modalTitle = getModalTitle(dialog);
    if (normalizeAnalyticsName(modalTitle) !== "modal") {
      return { name: `modal_${normalizeAnalyticsName(modalTitle)}`, label: modalTitle };
    }
  }

  const container = element.closest("form,section,article,[aria-label],[aria-labelledby]");
  if (container) {
    const labelledBy = container.getAttribute("aria-labelledby");
    const containerLabel = cleanText(
      container.dataset.analyticsName ||
      container.getAttribute("aria-label") ||
      (labelledBy ? document.getElementById(labelledBy)?.textContent : "") ||
      container.querySelector("h1,h2,h3,h4,legend")?.textContent,
    );
    if (containerLabel) {
      return { name: normalizeAnalyticsName(containerLabel), label: containerLabel };
    }
  }
  return { name: pageName, label: pageName };
};

export default function UserAnalyticsTracker() {
  const { isAuthenticated, isLoading, userId } = useContext(AuthContext);
  const location = useLocation();
  const lastPageKeyRef = useRef("");

  useEffect(() => {
    if (isLoading) return;
    configureUserAnalytics(isAuthenticated ? userId : null);
  }, [isAuthenticated, isLoading, userId]);

  useEffect(() => {
    if (!isAuthenticated || isLoading || !userId) return;
    const pageName = getPageAnalyticsName(location.pathname);
    const pageKey = `${userId}:${location.pathname}:${location.search}`;
    if (lastPageKeyRef.current === pageKey) return;
    lastPageKeyRef.current = pageKey;
    trackUserAction({
      actionType: "page_view",
      actionName: pageName,
      pagePath: location.pathname,
      metadata: { search_present: Boolean(location.search) },
    });
  }, [isAuthenticated, isLoading, location.pathname, location.search, userId]);

  useEffect(() => {
    if (!isAuthenticated || isLoading || !userId) return undefined;

    const pageName = getPageAnalyticsName(location.pathname);
    let lastInteraction = null;

    const handleClick = (event) => {
      const target = event.target instanceof Element ? event.target.closest(INTERACTIVE_SELECTOR) : null;
      if (!target || target.closest("[data-analytics-ignore]") || target.matches(":disabled")) return;
      const rawLabel = getElementLabel(target);
      const explicitType = target.dataset.analyticsType;
      const actionType = explicitType === "feature_launch" || explicitType === "action"
        ? explicitType
        : "button_click";
      const context = getInteractionContext(target, pageName);
      const normalizedLabel = normalizeAnalyticsName(rawLabel, "unknown_click");
      const actionName = target.dataset.analyticsName
        ? normalizedLabel
        : `${context.name}__${normalizedLabel}`.slice(0, 128);
      lastInteraction = { name: actionName, label: rawLabel, occurredAt: Date.now() };
      trackUserAction({
        actionType,
        actionName,
        pagePath: location.pathname,
        metadata: {
          label: rawLabel.slice(0, 160),
          element: target.tagName.toLowerCase(),
          context: context.label,
          page_name: pageName,
          aria_label: cleanText(target.getAttribute("aria-label")) || undefined,
          title: cleanText(target.getAttribute("title")) || undefined,
          href: target instanceof HTMLAnchorElement ? cleanText(target.getAttribute("href"), 300) || undefined : undefined,
        },
      });
    };

    const modalVisibility = new WeakMap();
    const scanModals = () => {
      document.querySelectorAll(MODAL_SELECTOR).forEach((modal) => {
        if (modal.matches(".MuiModal-root") && modal.querySelector("[role='dialog']")) return;
        const visible = isVisibleModal(modal);
        const wasVisible = modalVisibility.get(modal) === true;
        modalVisibility.set(modal, visible);
        if (!visible || wasVisible) return;
        const title = getModalTitle(modal);
        const normalizedTitle = normalizeAnalyticsName(title);
        const recentInteraction = lastInteraction && Date.now() - lastInteraction.occurredAt < 2000
          ? lastInteraction
          : null;
        const actionName = normalizedTitle === "modal" && recentInteraction
          ? `modal_after_${recentInteraction.name}`.slice(0, 128)
          : `modal_${normalizedTitle}`.slice(0, 128);
        trackUserAction({
          actionType: "modal_open",
          actionName,
          pagePath: location.pathname,
          metadata: {
            title: title.slice(0, 160),
            page_name: pageName,
            opened_by: recentInteraction?.name,
            opened_by_label: recentInteraction?.label,
          },
        });
      });
    };

    const observer = new MutationObserver(scanModals);
    document.addEventListener("click", handleClick, true);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "aria-hidden"],
    });
    scanModals();

    const handlePageHide = () => void flushUserAnalytics({ keepalive: true });
    const handleOnline = () => void flushUserAnalytics();
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("online", handleOnline);
    return () => {
      document.removeEventListener("click", handleClick, true);
      observer.disconnect();
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("online", handleOnline);
    };
  }, [isAuthenticated, isLoading, location.pathname, userId]);

  return null;
}
